import {
  DocumentTextIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useEffect } from 'react';
import MediaUploadService from '../../services/messaging/MediaUploadService';

const isImage = (attachment) => (
  attachment?.kind === 'image'
  || attachment?.mimeType?.startsWith('image/')
  || /\.(png|jpe?g|webp|gif|bmp|avif|heic|heif|svg)$/i.test(attachment?.fileName || '')
);

const fileTypeLabel = (attachment) => {
  const extension = attachment?.fileName?.split('.').pop()?.toUpperCase();
  if (extension && extension.length <= 5) return extension;
  const kind = MediaUploadService.getFileType(attachment?.mimeType);
  return kind === 'doc' ? 'DOC' : 'FILE';
};

const fileTypeClasses = (attachment) => {
  const type = fileTypeLabel(attachment);
  if (type === 'PDF') return 'bg-error/10 text-error';
  if (type === 'DOC' || type === 'DOCX') return 'bg-info/10 text-info';
  return 'bg-base-200 text-base-content/60';
};

function RemoveButton({ attachment, onRemove, disabled }) {
  if (!onRemove) return null;
  return (
    <button
      type="button"
      className="btn btn-circle btn-xs border border-base-300 bg-base-100/95 text-base-content/60 opacity-60 shadow-sm transition-opacity hover:bg-base-100 hover:opacity-100 [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-90 [@media(hover:hover)]:focus-visible:opacity-100"
      onClick={() => onRemove(attachment.id)}
      disabled={disabled}
      aria-label={`Remove ${attachment.fileName || 'attachment'}`}
    >
      <XMarkIcon className="h-4 w-4" />
    </button>
  );
}

function PendingStatus({ attachment }) {
  const progressWidth = attachment.progress > 0
    ? `${Math.max(4, attachment.progress)}%`
    : '28%';

  return (
    <div className="absolute inset-x-0 bottom-0 h-1 overflow-hidden bg-base-content/20" aria-hidden="true">
      <div
        className={`h-full rounded-r-full ${attachment.status === 'waiting' ? 'bg-base-content/35' : 'bg-primary'} ${attachment.progress === 0 ? 'animate-pulse' : ''}`}
        style={{ width: progressWidth }}
      />
    </div>
  );
}

function ResolvedImage({ attachment, localPreviewUrl, onPreviewResolved }) {
  const remoteUrl = attachment.thumbnailUrl || attachment.url;

  useEffect(() => {
    if (!localPreviewUrl || !remoteUrl) return undefined;
    const remoteImage = new Image();
    remoteImage.onload = () => onPreviewResolved?.(attachment.id);
    remoteImage.src = remoteUrl;
    return () => {
      remoteImage.onload = null;
    };
  }, [attachment.id, localPreviewUrl, onPreviewResolved, remoteUrl]);

  return (
    <img
      src={localPreviewUrl || remoteUrl}
      alt=""
      className="h-full w-full object-cover"
    />
  );
}

export default function AttachmentComposerTray({
  attachments = [],
  pending = [],
  localPreviews = {},
  onPreviewResolved,
  onRemove,
  disabled = false,
}) {
  if (!attachments.length && !pending.length) return null;

  return (
    <div
      className="flex w-full min-w-0 gap-2 overflow-x-auto pb-1 pr-2 pt-2"
      data-attachment-composer-tray
      aria-label="Selected attachments"
    >
      {attachments.map(attachment => (
        <div
          key={attachment.id}
          className="group relative h-24 w-24 shrink-0"
          data-attachment-tile
        >
          {isImage(attachment) ? (
            <a
              href={attachment.url || attachment.thumbnailUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="block h-full w-full overflow-hidden rounded-xl border border-base-300 bg-base-200"
              aria-label={`Open ${attachment.fileName || 'image'}`}
            >
              <ResolvedImage
                attachment={attachment}
                localPreviewUrl={localPreviews[attachment.id]}
                onPreviewResolved={onPreviewResolved}
              />
            </a>
          ) : (
            <a
              href={attachment.url}
              target="_blank"
              rel="noopener noreferrer"
              download={attachment.fileName}
              className={`flex h-full w-full flex-col items-center justify-center overflow-hidden rounded-xl border border-base-300 p-2 text-center ${fileTypeClasses(attachment)}`}
              aria-label={`Open ${attachment.fileName || 'file'}`}
              title={attachment.fileName}
            >
              <DocumentTextIcon className="h-6 w-6" />
              <span className="mt-1 text-[10px] font-semibold tracking-wide">{fileTypeLabel(attachment)}</span>
              <span className="mt-1 w-full truncate text-[10px] font-normal normal-case tracking-normal text-base-content/60">
                {attachment.fileName}
              </span>
              <span className="mt-0.5 text-[9px] font-normal normal-case tracking-normal text-base-content/45">
                {MediaUploadService.formatFileSize(attachment.fileSize)}
              </span>
            </a>
          )}
          <div className="absolute -right-2 -top-2">
            <RemoveButton attachment={attachment} onRemove={onRemove} disabled={disabled} />
          </div>
        </div>
      ))}

      {pending.map(attachment => (
        <div
          key={attachment.id}
          className="relative h-24 w-24 shrink-0 overflow-hidden rounded-xl border border-base-300 bg-base-200"
          aria-label={`Uploading ${attachment.fileName}`}
          data-attachment-tile
        >
          {isImage(attachment) ? (
            <>
              {attachment.previewUrl ? (
                <img
                  src={attachment.previewUrl}
                  alt=""
                  className="h-full w-full scale-105 object-cover blur-[2px] saturate-50"
                />
              ) : (
                <div className="h-full w-full animate-pulse bg-base-300" />
              )}
              <div className="absolute inset-0 bg-base-100/15" />
            </>
          ) : (
            <div className={`flex h-full w-full flex-col items-center justify-center p-2 text-center ${fileTypeClasses(attachment)}`}>
              <DocumentTextIcon className="h-6 w-6" />
              <span className="mt-1 text-[10px] font-semibold tracking-wide">{fileTypeLabel(attachment)}</span>
              <span className="mt-1 w-full truncate text-[10px] font-normal normal-case tracking-normal text-base-content/60">
                {attachment.fileName}
              </span>
              <span className="mt-0.5 text-[9px] font-normal normal-case tracking-normal text-base-content/45">
                {MediaUploadService.formatFileSize(attachment.fileSize)}
              </span>
            </div>
          )}
          <PendingStatus attachment={attachment} />
        </div>
      ))}
    </div>
  );
}
